// components/HelpChatPortalComponent.jsx
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import ChatExperience from '../../../support-deflection/ChatExperience';
import { useIdentity } from '../../hooks/useIdentity';
import SupportProvider, {
  SupportContext,
} from '../../../support-deflection/SupportContext';
import { Theme } from '../../theme';
import { Grid, TextField, Button } from '@mui/material';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import styled from '@emotion/styled';

const HelpChatReactContent = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const HelpChatComponent = ({ identityData }) => {
  const { t } = useTranslation();
  const supportContext = useContext(SupportContext);

  if (!supportContext) {
    console.error('SupportContext is not available');
    return null;
  }

  const { question, setQuestion, handleUserRequest, isBotAnswering } =
    supportContext;

  return (
    <HelpChatReactContent className="help-chat-react-content">
      <Theme>
        <ChatExperience identityData={identityData} />

        <Grid
          item
          sm={5}
          alignItems="center"
          sx={{
            position: 'relative',
          }}
        >
          <Grid
            container
            alignItems="center"
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '5px',
              flexWrap: 'nowrap',
            }}
          >
            <Grid
              item
              sx={{
                flexGrow: '1',
              }}
            >
              <TextField
                onChange={(e) => setQuestion(e.target.value)}
                value={question}
                placeholder={t('GlobalNav.HelpChat.TypeYourQuestion')}
                fullWidth
                type="text"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleUserRequest();
                  }
                }}
                inputProps={{
                  minLength: 1,
                  maxLength: 512,
                }}
              />
            </Grid>
            <Grid
              item
              sx={{
                display: 'flex',
              }}
            >
              <Button
                variant="contained"
                startIcon={
                  <SendOutlinedIcon sx={{ color: 'white', height: '18px' }} />
                }
                onClick={() => {
                  handleUserRequest();
                }}
                type="submit"
                disabled={isBotAnswering}
                sx={{
                  '&.Mui-disabled': {
                    background: 'rgba(0, 0, 0, 0.12)',
                    color: 'rgba(0, 0, 0, 0.32)',
                    pointerEvents: 'auto',
                  },
                  textTransform: 'capitalize',
                  lineHeight: 1.96,
                }}
              >
                {t('GlobalNav.HelpChat.Send')}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Theme>
    </HelpChatReactContent>
  );
};

export function HelpChatPortalComponent({ menuElement }) {
  const { data: identityData } = useIdentity();
  const [contentContainer, setContentContainer] =
    useState<HTMLDivElement | null>(null);

  useEffect(() => {
    // Create a container for our React content with the slot attribute
    const container = document.createElement('div');
    container.slot = 'ai-chat';
    container.className = 'help-chat-content-container';
    container.style.height = '100%';

    menuElement.appendChild(container);
    setContentContainer(container);

    return () => {
      if (container && container.parentNode) {
        container.parentNode.removeChild(container);
      }
    };
  }, [menuElement]);

  // Only create the portal if we have a container
  if (!contentContainer) {
    return null;
  }

  return ReactDOM.createPortal(
    <SupportProvider>
      <HelpChatComponent identityData={identityData} />
    </SupportProvider>,
    contentContainer,
  );
}
